/**
 * 計算式で選択肢の表示名を出力値のつもりで書いていないかの検知（#3403）。
 *
 * 選択肢型（#3075）の result_data には「出力値」が入る。そのため次の2つは、
 * 出力値が別に設定されている選択肢では意図どおりに動かない。
 * - 参照側: IF({区分} = "有効", …) … {区分} は出力値で評価されるため一致しない
 * - 結果側: 選択肢型の計算フィールドで IF(…, "一致", "不一致") … 結果がそのまま
 *   保存され、出力値（OK / NG）と一致しないため候補外になる
 *
 * 評価側で表示名を出力値に読み替えることはしない。
 * ある選択肢の表示名が別の選択肢の出力値と同じ場合に判定が曖昧になるため、
 * 式の入力時にエラーとし、出力値で書くよう案内する（エラーの間は保存できない）。
 */
import { ItemChoiceType } from '../../types/common';
import { parseFormula } from '../calculation/parser';
import type { AstNode } from '../calculation/parser';
import { CalculationFormulaError } from '../calculation/calculationError';
import { resolveChoiceValue } from './choiceUtils';

/** 比較対象を検査する項目（選択肢を持つものだけ渡せばよい） */
export type FormulaChoiceItem = {
  fieldCode: string;
  choices?: ItemChoiceType[];
};

/** 一致・不一致を判定する比較演算子。大小比較は選択肢に対して意味を持たないため対象外 */
const EQUALITY_OPERATORS: string[] = ['=', '!='];

export const formulaChoiceErrorMessage = (
  fieldCode: string,
  label: string,
  value: string,
): string =>
  `{${fieldCode}} は出力値で比較されます。表示名「${label}」ではなく出力値 "${value}" を指定してください`;

export const formulaResultChoiceErrorMessage = (label: string, value: string): string =>
  `計算結果は出力値で保存されます。表示名「${label}」ではなく出力値 "${value}" を指定してください`;

/** 比較の相手がリテラルなら文字列として取り出す */
const toLiteralText = (node: AstNode): string | null => {
  if (node.type === 'string') return node.value.trim();
  if (node.type === 'number') return String(node.value);
  return null;
};

/** 表示名と一致し、出力値には一致しない選択肢を返す（出力値と一致していれば問題なし） */
const findLabelOnlyChoice = (
  literal: string,
  choices: ItemChoiceType[],
): ItemChoiceType | undefined => {
  if (choices.some(choice => resolveChoiceValue(choice) === literal)) return undefined;
  return choices.find(choice => String(choice.label ?? '').trim() === literal);
};

/** 表示名で比較している場合にエラー文言を返す */
const checkComparison = (
  fieldNode: AstNode,
  literalNode: AstNode,
  choicesByCode: Map<string, ItemChoiceType[]>,
): string | null => {
  if (fieldNode.type !== 'fieldRef') return null;
  const choices = choicesByCode.get(fieldNode.fieldCode);
  const literal = toLiteralText(literalNode);
  if (!choices || literal === null || literal === '') return null;

  const byLabel = findLabelOnlyChoice(literal, choices);
  if (!byLabel) return null;
  return formulaChoiceErrorMessage(fieldNode.fieldCode, literal, resolveChoiceValue(byLabel));
};

const collectErrors = (
  node: AstNode,
  choicesByCode: Map<string, ItemChoiceType[]>,
  warnings: string[],
): void => {
  switch (node.type) {
    case 'unary':
      collectErrors(node.operand, choicesByCode, warnings);
      return;
    case 'binary': {
      if (EQUALITY_OPERATORS.includes(node.operator)) {
        const warning =
          checkComparison(node.left, node.right, choicesByCode) ??
          checkComparison(node.right, node.left, choicesByCode);
        if (warning !== null && !warnings.includes(warning)) {
          warnings.push(warning);
        }
      }
      collectErrors(node.left, choicesByCode, warnings);
      collectErrors(node.right, choicesByCode, warnings);
      return;
    }
    case 'funcCall':
      node.args.forEach(arg => collectErrors(arg, choicesByCode, warnings));
      return;
    default:
      return;
  }
};

/**
 * 式の結果になりうるリテラルを集める。
 * 式全体がリテラルの場合と、IF の真・偽の枝（入れ子の IF も含む）を対象にする。
 * 文字列結合など加工された結果は、どの値になるか静的に決まらないため対象外
 */
const collectResultLiterals = (node: AstNode, literals: string[]): void => {
  if (node.type === 'funcCall' && node.name === 'IF') {
    node.args.slice(1).forEach(arg => collectResultLiterals(arg, literals));
    return;
  }
  const literal = toLiteralText(node);
  if (literal !== null && literal !== '') literals.push(literal);
};

/**
 * 選択肢の表示名を出力値のつもりで書いている箇所のエラー文言を返す。
 * 構文エラーの式は検証側（validateFormula）がエラーを出すため、ここでは何も返さない。
 */
export const findChoiceLabelComparisons = (
  formula: string,
  items: FormulaChoiceItem[],
  /** 編集中の計算フィールド自身の選択肢。選択肢型のときだけ渡す */
  selfChoices: ItemChoiceType[] = [],
): string[] => {
  const choicesByCode = new Map<string, ItemChoiceType[]>();
  items.forEach(item => {
    if (item.choices && item.choices.length > 0) {
      choicesByCode.set(item.fieldCode, item.choices);
    }
  });
  if ((choicesByCode.size === 0 && selfChoices.length === 0) || formula.trim() === '') return [];

  try {
    const ast = parseFormula(formula);
    const warnings: string[] = [];
    collectErrors(ast, choicesByCode, warnings);

    const resultLiterals: string[] = [];
    collectResultLiterals(ast, resultLiterals);
    resultLiterals.forEach(literal => {
      const byLabel = findLabelOnlyChoice(literal, selfChoices);
      if (!byLabel) return;
      const warning = formulaResultChoiceErrorMessage(literal, resolveChoiceValue(byLabel));
      if (!warnings.includes(warning)) warnings.push(warning);
    });
    return warnings;
  } catch (error) {
    if (error instanceof CalculationFormulaError) return [];
    throw error;
  }
};
